import React, { useState } from 'react';
import {
  Controller,
  useController,
  useFieldArray,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { range } from 'lodash-es';
import parsePhoneNumber, { getCountryCallingCode } from 'libphonenumber-js/max';
import PropTypes from 'prop-types';
import { Accordion, Button, Flex, Input, Text } from '@chakra-ui/react';
import { User as UserIcon } from '../../../../context/Chakra/Icons';
import { formatPhone } from '../../../../../utils/intlTel';
import { useAppContext } from '../../../context/appContext';
import CreditCardField from '../components/CreditCardField';
import EInvoice from '../components/EInvoice';
import FormField from '../components/FormField';
import SaveContact from '../components/SaveContact';
import PhoneField from '../components/PhoneField';
import { FIELD } from '../constants/field';

const MAX_ADDRESS_COUNT = 5;

const ContactInfoSection = (props) => {
  const { setAddressSchemaMap } = props;
  const { t } = useTranslation();
  const { requestCountry, slFeatureService, slPaymentBindCard } =
    useAppContext();
  const hasCountryCode = slFeatureService.hasFeature('country_code');
  const { control, errors, formState, getValues, register, setValue, trigger } =
    useFormContext();
  const phoneCountryController = useController({
    name: FIELD.PHONE_COUNTRY_CODE,
    control,
    defaultValue:
      getValues(FIELD.PHONE_COUNTRY_CODE) ||
      getCountryCallingCode(requestCountry),
  });
  const { append, fields, remove } = useFieldArray({
    control,
    name: FIELD.DELIVERY_ADDRESSES,
  });
  const parsePhone = parsePhoneNumber(
    `+${phoneCountryController.field.value}${getValues(FIELD.PHONE)}`,
  );
  const [countryCode, setCountryCode] = useState(
    parsePhone?.country || requestCountry,
  );
  const handleCountryChange = (currentNumber, { dialCode, iso2 }) => {
    const newCountry = iso2?.toLowerCase();
    if (newCountry !== countryCode.toLowerCase()) {
      setValue(FIELD.PHONE_COUNTRY_CODE, dialCode, { shouldDirty: true });
      setValue(FIELD.PHONE, formatPhone(currentNumber, newCountry), {
        shouldDirty: true,
        shouldValidate: formState.isSubmitted,
      });
      setCountryCode(newCountry);
    }
  };

  const handlePhoneNumberBlur = (_, phone, { iso2 }) => {
    if (phone.length === 0 || errors[FIELD.PHONE]) return;
    setValue(FIELD.PHONE, formatPhone(phone, iso2));
  };

  const handleAddAddress = () => {
    append({
      recipient_name: '',
      recipient_phone: '',
      recipient_phone_country_code: '',
    });
  };

  return (
    <Flex
      direction="column"
      gap="20px"
      padding="20px 20px 40px"
      border="1px solid #ddd"
      className="member-center-profile__form-section"
    >
      <Flex gap="10px" className="profile-section_title" alignItems="center">
        <UserIcon
          fontSize="20px"
          color="#dddddd"
          className="icon-user member-center-profile__section-title-icon"
        />
        <Text
          variant="sectionTitle"
          className="member-center-profile__section-title-text"
        >
          {t('member_profile.delivery_payment')}
        </Text>
      </Flex>
      <FormField
        label={`${t('member_profile.phone')} ${t(
          'membership.profile.optional_field',
        )}`}
        hasError={Boolean(errors[FIELD.PHONE])}
      >
        {hasCountryCode ? (
          <Controller
            name={FIELD.PHONE}
            control={control}
            defaultValue=""
            render={({ value, onChange }) => (
              <PhoneField
                value={value}
                onPhoneNumberChange={(_, newPhone) => {
                  onChange(newPhone);
                  if (formState.isSubmitted) {
                    trigger(FIELD.PHONE);
                  }
                }}
                onCountryChange={handleCountryChange}
                onPhoneNumberBlur={handlePhoneNumberBlur}
                defaultCountry={countryCode}
                e2eId="member-center-profile-contact-phone_field"
              />
            )}
          />
        ) : (
          <Input
            name={FIELD.PHONE}
            ref={register}
            onChange={() => {
              if (formState.isSubmitted) {
                trigger(FIELD.PHONE);
              }
            }}
            data-e2e-id="member-center-profile-contact-phone_field"
          />
        )}
        {errors[FIELD.PHONE] && (
          <Text variant="errorMessage" textAlign="right">
            {t(errors[FIELD.PHONE].message, {
              field_name: t('member_profile.phone'),
            })}
          </Text>
        )}
      </FormField>
      <FormField
        label={`${t('membership.profile.delivery_addresses')} ${t(
          'membership.profile.optional_field',
        )}`}
      >
        {fields.length < MAX_ADDRESS_COUNT ? (
          <Button
            mr="auto"
            my="12px"
            variant="textButton"
            className="member-center-profile__text-button"
            data-e2e-id="member-center-profile-add-contact-address_button"
            onClick={handleAddAddress}
          >
            {t('users.add.delivery_address')}
          </Button>
        ) : (
          <Text textAlign="left" fontStyle="italic">
            {t('users.add.delivery_address.maximum')}
          </Text>
        )}
        <Accordion allowMultiple defaultIndex={range(MAX_ADDRESS_COUNT)}>
          {fields.map((item, index) => (
            <SaveContact
              key={item.id}
              contactInfo={item}
              index={index}
              setAddressSchemaMap={setAddressSchemaMap}
              onDelete={remove}
            />
          ))}
        </Accordion>
      </FormField>
      {slPaymentBindCard.enabled && <CreditCardField />}
      <EInvoice />
    </Flex>
  );
};

ContactInfoSection.propTypes = {
  setAddressSchemaMap: PropTypes.func.isRequired,
};

export default ContactInfoSection;
